import { Authsignal } from '@authsignal/browser';

import { environment } from '@environments/environment';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { Observable, catchError, finalize, from, map, switchMap, throwError } from 'rxjs';

import { authRefreshShortTermTokenStart } from '@views/misc/auth/auth.actions';
import { ErrorKeys } from '@views/misc/auth/auth.types';
import { StorageService } from '@views/misc/auth/storage.service';

import { LoggerService } from '@core/utilities/logger/logger.service';

import * as actions from './interceptor.actions';
import { SpecialErrorCodes, interceptorModifiyResponse } from './interceptor.helper';
import { HttpErrorState } from './interceptor.types';

/**
 * How long an MFA challenge may sit unresolved before we report it. Generous enough to cover
 * a user leaving the app to fetch an SMS code and coming back.
 */
const MFA_WATCHDOG_MS = 180_000;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private store = inject(Store);
  private loggerService = inject(LoggerService);
  private storageService = inject(StorageService);

  private contentNegotiation = true;
  authsignal: Authsignal;

  constructor() {
    this.authsignal = new Authsignal({
      tenantId: environment.authSignalTenantId,
      baseUrl: environment.authSignalBaseUrl,
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('dc.services.visualstudio.com')) {
      // Skip interceptor if api call is for appInsights so that we don't get an infinite loop
      return next.handle(request);
    }

    /**
     * This MUST stay local to the call. The interceptor is a singleton, so holding the in-flight
     * request on the instance let every later request overwrite it — and the MFA retry below reads
     * it back only after the Authsignal pop-up resolves, seconds or minutes later. That replayed
     * whatever request happened to be last instead of the one that was challenged.
     */
    let modifiedRequest: HttpRequest<any>;

    if (this.contentNegotiation) {
      // remove any undefined params
      let params = request.params;
      for (const key of request.params.keys()) {
        if (params.get(key) === 'undefined') {
          params = params.delete(key);
        }
      }

      modifiedRequest = request.clone({
        setHeaders: {
          // let's add the squirrel.envelope to the header
          Accept: 'application/squirrel.envelope',
          // Auth signal device id is added to all headers
          'X-Multifactor-Authentication-Device-Id': this.authsignal?.anonymousId,
        },
        params,
      });
    } else {
      modifiedRequest = request;
    }

    return next.handle(modifiedRequest).pipe(
      map((res: HttpEvent<any>) => {
        if (this.contentNegotiation && request.responseType !== 'blob') {
          // Check short term token expiry
          if (res instanceof HttpResponse && res.headers.has('x-authtoken-expires')) {
            const tokenExpires = res.headers.get('x-authtoken-expires');
            if (tokenExpires !== undefined && Number(tokenExpires) < 600) {
              // If token has less that 10 mins(600 seconds) then refresh it
              const longTermToken = this.storageService.getLongToken();
              if (longTermToken) {
                this.store.dispatch(authRefreshShortTermTokenStart({ longTermToken }));
              }
            }
          }
          // Skip this step if response type is blob as its wont have a payload
          if (res instanceof HttpResponse) {
            const modifiedResponse = res.clone({
              body: res.body ? res.body.payload : null,
            });
            return modifiedResponse;
          }
          return res;
        } else {
          return res;
        }
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        let errorMessage = '';
        let modifiedError;
        let realError;
        if (errorResponse?.status?.toString() === SpecialErrorCodes.MfaRequired && errorResponse?.error?.payload?.url) {
          /**
           * ERROR 499: AuthSignal - if an api returns a 499 error it means it multifactor
           * authentication via an AuthSignal token is required. AuthSignal is launched via
           * the SDK and if succesful the token is added to the request and retried.
           */

          const modifiedAuthSignalError = {
            errors: [
              {
                status: SpecialErrorCodes.MfaRequired,
                errorMessage: 'Multi-factor authentication failed',
              },
            ],
            status: SpecialErrorCodes.MfaRequired,
          };
          /**
           * Nothing on this path is visible in telemetry otherwise: it raises no error when
           * it stalls, and Sentry's XHR breadcrumbs miss our traffic entirely on native
           * because CapacitorHttp replaces `XMLHttpRequest.prototype.send`. So each step
           * leaves a breadcrumb, and the outcomes worth chasing capture a warning to carry
           * that trail to Sentry. Never log the token itself — only whether we got one.
           */
          const challengedRequest = `${request.method} ${request.url}`;
          const challengeStartedAt = Date.now();
          let challengeSettled = false;

          this.loggerService.logBreadcrumb('mfa', 'Challenge received (499)', { request: challengedRequest });

          // A challenge that never settles leaves the caller hanging with no error at all.
          // Report it rather than let it vanish. Backgrounded webviews throttle timers, so
          // treat the delay as a floor, not a deadline.
          const challengeWatchdog = setTimeout(() => {
            if (!challengeSettled) {
              this.loggerService.logWarning('MFA challenge still unresolved', {
                request: challengedRequest,
                waitedMs: Date.now() - challengeStartedAt,
              });
            }
          }, MFA_WATCHDOG_MS);

          const settleChallenge = (): void => {
            challengeSettled = true;
            clearTimeout(challengeWatchdog);
          };

          this.store.dispatch(actions.interceptorMfaStarted());
          this.loggerService.logBreadcrumb('mfa', 'Pop-up launched');

          return from(
            this.authsignal.launch(errorResponse?.error?.payload?.url, {
              mode: 'popup',
              popupOptions: { width: '600px' },
              // Errors raised inside the pre-built UI are otherwise swallowed entirely.
              onError: params =>
                this.loggerService.logWarning('Authsignal pop-up reported an error', {
                  ...params,
                  request: challengedRequest,
                }),
            }),
          ).pipe(
            /**
             * Handles a launch that never produced a result. It sits before the switchMap on
             * purpose: chained after it, this would also catch everything the projection below
             * throws, reporting an ordinary cancellation or a failed retry as a launch failure.
             */
            catchError(() => {
              settleChallenge();
              this.loggerService.logWarning('MFA challenge failed before the retry was issued', {
                request: challengedRequest,
                elapsedMs: Date.now() - challengeStartedAt,
              });
              this.store.dispatch(actions.interceptorMfaEnded());
              return throwError(modifiedAuthSignalError);
            }),
            switchMap(authsignalResponse => {
              settleChallenge();
              const token = authsignalResponse?.token;
              const elapsedMs = Date.now() - challengeStartedAt;
              this.loggerService.logBreadcrumb('mfa', 'Pop-up resolved', { hasToken: Boolean(token), elapsedMs });

              if (!token) {
                /**
                 * The SDK resolves on dismissal as well as on success, so no token means the user
                 * closed the pop-up. Retrying with an empty token only spends a round trip to be
                 * told what we already know, so treat it as the cancellation it is.
                 */
                this.loggerService.logBreadcrumb('mfa', 'Pop-up dismissed without completing', { elapsedMs });
                this.store.dispatch(actions.interceptorMfaEnded());
                return throwError(modifiedAuthSignalError);
              }

              const modifiedRequestWithAuthSignal = modifiedRequest.clone({
                setHeaders: {
                  'X-Multifactor-Authentication-Token': token,
                },
              });

              /**
               * Canary, kept deliberately for a release or two. Now that the request is
               * captured in this call's closure these two cannot diverge, so this should never
               * fire — its silence across the device fleet is the confirmation the fix holds in
               * the wild, which no test can give us. Delete it once that is established.
               */
              const replayedRequest = `${modifiedRequestWithAuthSignal.method} ${modifiedRequestWithAuthSignal.url}`;
              if (replayedRequest !== challengedRequest) {
                this.loggerService.logWarning('MFA retry is replaying a different request than the one challenged', {
                  challenged: challengedRequest,
                  replayed: replayedRequest,
                });
              }
              this.loggerService.logBreadcrumb('mfa', 'Retry issued', { request: replayedRequest });

              this.store.dispatch(actions.interceptorMfaEnded());
              return next.handle(modifiedRequestWithAuthSignal).pipe(
                map((res: HttpEvent<any>) => {
                  return interceptorModifiyResponse(res, this.contentNegotiation, request);
                }),
                catchError(() => {
                  this.loggerService.logWarning('MFA retry failed', {
                    challenged: challengedRequest,
                    replayed: replayedRequest,
                    elapsedMs,
                  });
                  return throwError(modifiedAuthSignalError);
                }),
              );
            }),
            // Also covers unsubscription — a caller walking away mid-challenge would otherwise
            // leave the watchdog armed to report a challenge nobody is waiting for any more.
            finalize(() => settleChallenge()),
          );
        }
        if (this.contentNegotiation) {
          // TODO: remove after back-end team fix the return msg for this scenario
          // exception for expired offer, as the BE is returning a diff payload
          if (errorResponse?.status === 404) {
            if (errorResponse?.error?.error === 'Investment sell offer has expired') {
              const newError = {
                errors: [
                  {
                    errorCode: 400,
                    errorMessage: errorResponse?.error?.error,
                    propertyName: 'isOfferExpired',
                  },
                ],
              };
              return throwError(newError);
            }
          }
          // -------------------------------------------------------------------

          modifiedError = new HttpErrorResponse({
            error: {
              errors: errorResponse?.error ? errorResponse?.error?.errors : errorResponse?.error,
            },
            headers: errorResponse?.headers,
            status: errorResponse?.status,
            statusText: errorResponse?.statusText,
            url: errorResponse?.url || undefined,
          });
        } else {
          modifiedError = new HttpErrorResponse({
            error: { errors: errorResponse?.error ? errorResponse?.error : errorResponse },
            headers: errorResponse?.headers,
            status: errorResponse?.status,
            statusText: errorResponse?.statusText,
            url: errorResponse?.url || undefined,
          });
        }

        if (modifiedError.error instanceof ErrorEvent) {
          // client-side error
          errorMessage = `Error: ${modifiedError?.error?.message}`;
          this.loggerService.logEvent({ message: `Client-side? Error: ${errorMessage}` });
        } else {
          // server-side error
          if (modifiedError.status === 400) {
            if (request.url.search('v2/login') !== -1) {
              // authentication error

              // if more than one login error, we look for invalid credentials and
              // throw this one first
              if (modifiedError.error.errors.length) {
                const errors = modifiedError.error.errors;
                let newError: { errors: HttpErrorState[] } = { errors: [] };
                errors.forEach((err: HttpErrorState) => {
                  if (err.errorMessage === ErrorKeys.invalidCredentials) {
                    errorMessage = 'You have entered an incorrect email or password.';
                    newError = {
                      errors: [
                        {
                          errorCode: err.errorCode,
                          errorMessage,
                          propertyName: 'email',
                        },
                      ],
                    };

                    // we want only this error to be displayed in the form
                    this.store.dispatch(actions.interceptorAddErrors({ payload: newError }));
                    return;
                  }

                  if (err.errorMessage === ErrorKeys.tooManyAttempts) {
                    errorMessage = 'Account locked for 15 minutes due to multiple unsuccessful login attempts.';
                    newError = {
                      errors: [
                        {
                          errorCode: err.errorCode,
                          errorMessage,
                          propertyName: 'email',
                        },
                      ],
                    };
                    this.store.dispatch(actions.interceptorAddErrors({ payload: newError }));
                    return;
                  }
                });
                if (newError.errors.length) {
                  return throwError(newError);
                }
              }
              // no invalid credentials when multiple errors returned, let's get the first error only
              realError = modifiedError.error.errors[0];
            } else {
              // for any other page, we do care about all errors
              realError = modifiedError.error.errors;

              const errors: HttpErrorState[] = [];
              realError.forEach((err: HttpErrorState) => {
                // if different API version, will return entity.property
                // in this case we just need to remove the entity part of
                // the propertyName to match our form fields
                const propName =
                  err.propertyName.search(/\./g) >= 0 ? err.propertyName.split('.')[1] : err.propertyName;
                let errorCode = err.errorCode;
                if (err.errorMessage.includes('Your ID has expired')) {
                  // Add special errorcode 1001 for expired id,
                  // this is a short term fix as we don't have a good solution for
                  // finding the error we have to use a string match.
                  // Should be replace with better solution from backend;
                  errorCode = SpecialErrorCodes.IdExpired;
                } else if (err.errorCode === 'InvalidValue') {
                  // If the errorCode is InvalidValue this means the error is from
                  // an invalid form value so we will give it a special errorCode
                  errorCode = SpecialErrorCodes.InvalidValue;
                }
                errors.push({
                  errorCode,
                  errorMessage: err.errorMessage,
                  propertyName: propName,
                });
              });

              const newError = { errors };

              // we want this error to be displayed in the form
              this.store.dispatch(actions.interceptorAddErrors({ payload: newError }));

              return throwError(newError);
            }
          }

          if (modifiedError.status === 401) {
            // probably tokens expired, need to force logout
            const newError = {
              errors: [
                {
                  errorCode: '401',
                  errorMessage: 'Authentication expired',
                  propertyName: 'login',
                },
              ],
            };

            return throwError(newError);
          }

          if (modifiedError.status === 404) {
            return throwError(modifiedError);
          }
        }
        return throwError(modifiedError);
      }),
    );
  }
}
